import React, { useEffect, useState } from "react";
import { ReportSummaryItemApi } from "../api/Reports";
import {
  AffiliationItemWithMetaData,
  CreateAffiliationApi,
  Item,
  UpdateAffiliationApi,
} from "../api/Items";
import { DropdownItem } from "../shared-components/DropdownCombo";
import { validURL } from "../utils";
import PageHeader from "../shared-components/PageHeader";

import InputField from "../shared-components/InputField";
import TextAreaField from "../shared-components/TextAreaField";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";

import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { behaviorMap } from "../api/FamaFlags";
import PersonFormStringDate from "../persons/PersonFormStringDate";
import DropdownComboSimple from "../shared-components/DropdownComboSimple";
import {
  optimizeImage,
  OptimizedImageResult,
  MAX_DESCRIPTION_LIMIT,
  SOURCE_OPTIONS,
  ACTIVITY_OPTIONS,
  AddAffiliationProps,
  formErrors,
  imageUrlExternalToUrlApi,
} from "./helpers";
import AffiliationFormErrors from "./AffiliationFormErrors";
import ImageDisplay from "./ImageDisplay";
import {
  affiliationItemToAffiliationApi,
  useAffiliationManagement,
} from "./useAffiliationManagement";

export interface AffiliationModalState {
  report: ReportSummaryItemApi;
  affiliationContent?: AffiliationItemWithMetaData;
}

type Props = {
  showModal: boolean;
  close: (item?: Item) => void;
  initialState: AffiliationModalState;
  behaviors?: string[];
  isNew?: boolean;
  keywordUUID?: string;
  showDateError?: boolean;
};

const ModalAffiliationAdd = ({
  showModal,
  close,
  initialState,
  behaviors,
  isNew,
  showDateError = false,
}: Props) => {
  return (
    <Modal open={showModal} onClose={close} width={"w-full max-w-2xl p-3"}>
      <AffiliationForm
        report={initialState.report}
        behaviors={behaviors}
        onClose={close}
        affiliationContent={initialState?.affiliationContent}
        isNew={isNew}
        showDateError={showDateError}
      />
    </Modal>
  );
};

const AffiliationForm = ({
  report,
  behaviors,
  onClose,
  affiliationContent,
  isNew,
  showDateError = false,
}: AddAffiliationProps) => {
  const isEdit = !!affiliationContent;

  const [imageList, setImageList] = useState<
    { imageSrc: string; imageUrl: string }[]
  >(
    affiliationContent?.item.content.media_urls.map((url: string) => ({
      imageSrc: url,
      imageUrl: imageUrlExternalToUrlApi(url),
    })) ?? []
  );

  const [behaviourOptions, setBehaviourOptions] = useState<DropdownItem[]>([]);

  const [affiliation, setAffiliation] = useState<CreateAffiliationApi>(
    affiliationItemToAffiliationApi(report, affiliationContent)
  );

  const [errors, setErrors] = useState<formErrors>({
    affiliationType: false,
    affiliationSource: false,
    affiliationActivity: false,
    url: false,
    imageUrl: false,
    text: false,
    ratings: false,
    date: false,
    submissionTried: false,
  });

  const {
    createAffiliation,
    isLoadingAffiliationCreating,
    affiliationCreationError,
    successAffiliation,
    createdAffiliationUUID,
    resetCreateAffiliation,
    updateAffiliation,
    isLoadingAffiliationUpdate,
    updateAffiliationError,
    successUpdateAffiliation,
    updatedAffiliation,
    resetUpdateAffiliation,
    isLoadingImagePresignedUrl,
    isLoadingImageUploading,
    uploadImageToS3,
    getImagePresignedUrl,
    behaviorMappingActions,
  } = useAffiliationManagement();

  useEffect(() => {
    if (successAffiliation && createdAffiliationUUID) {
      onClose(createdAffiliationUUID);
      resetCreateAffiliation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successAffiliation, onClose]);

  useEffect(() => {
    if (successUpdateAffiliation && updatedAffiliation) {
      onClose(updatedAffiliation);
      resetUpdateAffiliation();

      if (affiliationContent) {
        affiliationContent.item.content = { ...updatedAffiliation.content };
        affiliationContent.rating = affiliation.ratings.map((p) => ({
          external_id: "",
          rater_uuid: "",
          name: p.name,
          score_type: p.score_type,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdateAffiliation, onClose]);

  useEffect(() => {
    if (behaviors) {
      const newBehaviors: string[] = [];
      behaviors.forEach((behavior) => {
        if (!newBehaviors.includes(behavior)) {
          newBehaviors.push(behavior);
        }
      });

      const newBehaviorOptions = newBehaviors
        .map((value, index) => {
          return { id: index.toString(), label: value };
        })
        .sort((itemA, itemB) => itemA.label.localeCompare(itemB.label));

      setBehaviourOptions(newBehaviorOptions);
    }
  }, [behaviors]);

  useEffect(() => {
    // Form Validation
    const affiliationTypeError = affiliation.affiliation_type.length === 0;
    const affiliationSourceError = affiliation.affiliation_source.length === 0;
    const affiliationActivityError =
      affiliation.affiliation_activity.length === 0;

    const urlError = !validURL(affiliation.url);

    setErrors((errors) => ({
      ...errors,
      affiliationType: affiliationTypeError,
      affiliationSource: affiliationSourceError,
      affiliationActivity: affiliationActivityError,
      url: urlError,
    }));
  }, [setErrors, affiliation]);

  useEffect(() => {
    if (showDateError) {
      setErrors((prev) => ({
        ...prev,
        date: true,
        submissionTried: true,
      }));
    }
  }, [showDateError]);

  const onRatingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRating = e.target.name;

    // If checked is removed, remove from Set
    if (!e.target.checked) {
      const newRatings = affiliation.ratings.filter((rating) => {
        return rating.name !== newRating;
      });
      setAffiliation({ ...affiliation, ratings: newRatings });
    } else {
      const newRatings = affiliation.ratings;
      newRatings.push({ name: newRating, score_type: "behavior" });
      setAffiliation({ ...affiliation, ratings: newRatings });
    }
  };

  const handleSavePost = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setErrors({ ...errors, submissionTried: true });

    if (
      !errors.url &&
      !errors.affiliationType &&
      !errors.affiliationSource &&
      !errors.affiliationActivity &&
      !errors.imageUrl &&
      !errors.text &&
      !errors.ratings &&
      !errors.date
    ) {
      affiliation.media_urls = imageList.map((image) => image.imageUrl);
      if (!isEdit) createAffiliation(affiliation);
      if (affiliationContent) {
        const updatedContent: CreateAffiliationApi = Object.assign(
          affiliationContent.item.content
        );
        updatedContent.url = affiliation.url;
        updatedContent.affiliation_source =
          affiliation.affiliation_source.substring(0, 255);
        updatedContent.affiliation_activity =
          affiliation.affiliation_activity.substring(0, 255);
        updatedContent.affiliation_type =
          affiliation.affiliation_type.substring(0, 255);
        updatedContent.text = affiliation.text.substring(
          0,
          MAX_DESCRIPTION_LIMIT
        );
        if (affiliation.created_at) {
          updatedContent.created_at = affiliation.created_at;
        }

        updatedContent.media_urls = affiliation.media_urls;
        const updateInput: UpdateAffiliationApi = {
          item_uuid: affiliationContent.item.external_id,
          content: updatedContent,
          ratings: affiliation.ratings.map((rating) => {
            return {
              name: rating.name,
              score_type: rating.score_type,
            };
          }),
          keywords: affiliationContent.keywords,
        };

        updateAffiliation(updateInput);
      }
    }
  };

  const showError = (): boolean => {
    const formError =
      errors.affiliationType ||
      errors.affiliationSource ||
      errors.affiliationActivity ||
      errors.url ||
      errors.imageUrl ||
      errors.ratings ||
      errors.text ||
      errors.date;
    return (
      errors.submissionTried &&
      (formError || !!affiliationCreationError || !!updateAffiliationError)
    );
  };

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  function isBehaviorChecked(label: string): boolean {
    return !!affiliation.ratings.find((rating) => rating.name === label);
  }

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    try {
      if (!navigator.clipboard) {
        console.error("Clipboard API not available");
        return;
      }

      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        const imageTypes = clipboardItem.types.find((type) =>
          type.startsWith("image/")
        );
        if (imageTypes) {
          const blob = await clipboardItem.getType(imageTypes);

          try {
            const optimizedImageResult: OptimizedImageResult =
              await optimizeImage(blob);

            const presignedUrl = await getImagePresignedUrl({
              report_uuid: report.report_uuid,
              file_name: optimizedImageResult.file.name,
            });

            await uploadImageToS3({
              uploadUrl: presignedUrl,
              file: optimizedImageResult.file,
            });

            const imageUrl = imageUrlExternalToUrlApi(presignedUrl);
            setImageList((prev) => [
              ...prev,
              {
                imageSrc: optimizedImageResult.blobUrl,
                imageUrl: `${imageUrl}`,
              },
            ]);
          } catch (error) {
            console.error("Failed to optimize image:", error);
          }

          break; // Assuming we need the first image
        }
      }
    } catch (err) {
      console.error("Failed to read clipboard:", err);
    }
  };

  return (
    <div onPaste={handlePaste}>
      <PageHeader title={isEdit ? "Edit Affiliation" : "Add Affiliation"} />
      <form>
        <div className="flex justify-between">
          <InputField
            classes="w-full"
            className="w-full mb-4 mr-8"
            handleChange={(e) => {
              setAffiliation({
                ...affiliation,
                affiliation_type: e.target.value,
              });
            }}
            name={"Affiliation Type"}
            placeholder="Type here..."
            label={"Affiliation Type *"}
            labelClassName="font-bold"
            value={affiliation.affiliation_type}
          />
        </div>

        <div className="flex justify-between">
          <InputField
            classes="w-full"
            className="w-full mb-4 mr-8"
            handleChange={(e) => {
              setAffiliation({ ...affiliation, url: e.target.value });
            }}
            name={"URL"}
            placeholder="Insert Url Address here..."
            label={"URL*"}
            labelClassName="font-bold"
            value={affiliation.url}
          />
        </div>
        <div className="mb-4">
          <DropdownComboSimple
            items={SOURCE_OPTIONS}
            onChange={(e) => {
              setAffiliation({ ...affiliation, affiliation_source: e.label });
            }}
            name={"affiliation_source"}
            label={"Source *"}
            labelClassName="font-bold"
            initial={
              SOURCE_OPTIONS.find(
                (option) => option.label === affiliation.affiliation_source
              ) ?? undefined
            }
          />
        </div>
        <div className="flex justify-between mb-4">
          <PersonFormStringDate
            _name="date"
            _label="Date"
            index={0}
            prefill={affiliation.created_at}
            onChange={(dateValue) => {
              setAffiliation({
                ...affiliation,
                created_at: dateValue,
              });
            }}
            classes="w-36"
          />
        </div>
        <div className="mb-4">
          <DropdownComboSimple
            items={ACTIVITY_OPTIONS}
            onChange={(e) => {
              setAffiliation({ ...affiliation, affiliation_activity: e.label });
            }}
            name={"affiliation_activity"}
            label={"Activity *"}
            labelClassName="font-bold"
            initial={
              ACTIVITY_OPTIONS.find(
                (option) => option.label === affiliation.affiliation_activity
              ) ?? undefined
            }
          />
        </div>
        <ImageDisplay imageList={imageList} setImageList={setImageList} />

        <div title="Description" className="">
          <TextAreaField
            classes="w-full"
            handleChange={(e) => {
              setAffiliation({ ...affiliation, text: e.target.value });
            }}
            name={"text"}
            placeholder="Type here..."
            label={"Description"}
            type="text"
            className="h-28 w-full"
            labelClassName="font-bold"
            value={affiliation.text}
          />
          {affiliation.text.length > MAX_DESCRIPTION_LIMIT && (
            <div className="bg-red-200 text-red-400 text-sm p-4">
              Your description is longer than {MAX_DESCRIPTION_LIMIT}{" "}
              characters, please reduce text before saving.
            </div>
          )}
        </div>

        <div title="Flagged Content" className="">
          <div className="block text-sm font-medium text-gray-600 mt-4">
            Behavior Flag
          </div>
          <div className="grid grid-cols-3 border border-1 border-gray-300 rounded-md p-2 mt-2">
            {behaviourOptions.map((behaviour) => (
              <LabeledCheckbox
                name={behaviour.label}
                label={behaviorMap(
                  behaviour.label,
                  behaviorMappingActions,
                  "key"
                )}
                classes="m-2"
                onChange={onRatingChange}
                key={behaviour.id}
                checked={isBehaviorChecked(behaviour.label)}
              ></LabeledCheckbox>
            ))}
          </div>
        </div>

        <AffiliationFormErrors
          errors={errors}
          showError={showError()}
          successAffiliation={successAffiliation}
          affiliationCreationError={affiliationCreationError}
          updateAffiliationError={updateAffiliationError}
        />
      </form>

      <div className="w-full border-t border-gray-300"></div>

      <div className="flex items-center justify-end pt-4 gap-3">
        <form>
          <Button
            type={ButtonTypes.default}
            text="Cancel"
            colorType={ButtonColorTypes.cancel}
            onClick={handleClose}
          />
        </form>
        <form className="flex-2">
          {isLoadingAffiliationCreating ||
          isLoadingAffiliationUpdate ||
          isLoadingImageUploading ||
          isLoadingImagePresignedUrl ? (
            <LoadingSpinner />
          ) : (
            <Button
              text={isNew ? "Save & Add" : "Save"}
              onClick={handleSavePost}
              colorType={ButtonColorTypes.success}
            />
          )}
        </form>
      </div>
    </div>
  );
};

export default ModalAffiliationAdd;
